/**
 * Phase 94 → Oct 2026 — the "Bulk Save Changes" bar is now a floating action
 * bar: fixed to the bottom-centre of the viewport (portal'd to <body> so no
 * table overflow/transform can clip it), visible only while a ledger has
 * unsaved edits, and always on screen regardless of scroll. Sits above the
 * mobile bottom tab bar; `raised` lifts it clear of the bulk-selection pill.
 */
import React from "react";
import { createPortal } from "react-dom";
import { AlertCircle } from "lucide-react";

export default function BulkSaveBar({ count, saving, onSave, onDiscard, testIdPrefix = "ledger", raised = false }) {
  if (!count) return null;
  return createPortal(
    <div
      className={`fixed left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1.5rem)] sm:w-auto sm:min-w-[380px] max-w-[640px] ${raised ? "bottom-[calc(8.5rem+env(safe-area-inset-bottom))] lg:bottom-24" : "bottom-[calc(4.5rem+env(safe-area-inset-bottom))] lg:bottom-6"}`}
      data-testid={`${testIdPrefix}-bulk-save-bar`}
      role="status"
    >
      <div className="flex items-center justify-between gap-3 rounded-sm border-2 border-[#F54800] bg-orange-50/95 backdrop-blur px-4 py-2.5 shadow-[0_12px_32px_-8px_rgba(245,72,0,0.45)] animate-in fade-in slide-in-from-bottom-2">
        <div className="text-sm font-semibold text-[#F54800] inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-[#F54800] text-white text-xs font-bold font-mono" data-testid={`${testIdPrefix}-bulk-save-count`}>{count}</span>
          <AlertCircle size={15} className="hidden sm:block" />
          <span className="whitespace-nowrap"><span className="sm:hidden">unsaved</span><span className="hidden sm:inline">{count === 1 ? "row" : "rows"} with unsaved changes</span></span>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={onDiscard}
            className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 text-slate-600 bg-white/70 hover:bg-white transition-colors"
            data-testid={`${testIdPrefix}-discard-btn`}
          >
            Discard
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="text-xs px-4 py-1.5 rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white font-semibold disabled:opacity-60 transition-colors"
            data-testid={`${testIdPrefix}-bulk-save-btn`}
          >
            {saving ? "Saving…" : "Bulk Save Changes"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
